import {
  ClockClockwiseIcon,
  CookieIcon,
  CurrencyCircleDollarIcon,
  FileTextIcon,
  HouseIcon,
  ReceiptIcon,
  SignOutIcon,
  SwapIcon,
  WalletIcon,
  VaultIcon,
  BankIcon,
} from "@phosphor-icons/react";
import React from "react";
import { FEATURE_FLAGS } from "./feature-flags";

export interface IMenuLink {
  icon?: React.ReactNode;
  name: string;
  link: string;
  class?: string;
  featureFlag?: string;
}

export interface IMenuItem {
  label: string | React.ReactNode;
  links?: IMenuLink[];
  class?: string;
}

export const menu_define = (t: (key: string) => string): IMenuItem[] => [
  // Mon Portefeuille (standalone)
  {
    label: null,
    links: [
      {
        icon: <HouseIcon size={24} color="#000" weight="fill" />,
        name: `${t("sidebar.my_portfolio")}`,
        link: "/",
      },
    ],
    class: "sidebar-section-no-padding",
  },
  // Compte Principal
  {
    label: `${t("sidebar.main_account_group")}`,
    links: [
      {
        icon: <CurrencyCircleDollarIcon size={24} weight={"thin"} />,
        name: `${t("sidebar.deposit_account")}`,
        link: "/main-account/",
        featureFlag: "ENABLE_ASSET_MANAGEMENT",
      },
    ],
  },
  // Gestion d'Actifs
  {
    label: `${t("sidebar.asset_management")}`,
    links: [
      {
        icon: <WalletIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.managed_portfolio")}`,
        link: "/management-mandate/",
        featureFlag: "ENABLE_MANDATE_FEATURE",
      },
      {
        icon: <ClockClockwiseIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.scheduled_investment")}`,
        link: "/dca-strategy/",
        featureFlag: "ENABLE_DCA_FEATURE",
      },
    ],
  },
  // Gestion de Trésorerie
  {
    label: `${t("sidebar.cash_management")}`,
    links: [
      {
        icon: <VaultIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.liquidity_savings")}`,
        link: "/treasury/",
        featureFlag: "ENABLE_TREASURY_FEATURE",
      },
      {
        icon: <BankIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.liquidity_borrowing")}`,
        link: "/lending/",
        featureFlag: "ENABLE_LENDING_FEATURE",
      },
    ],
  },
  // Achat de Crypto
  ...(FEATURE_FLAGS.EXCHANGE_ENABLED
    ? [
        {
          label: `${t("sidebar.crypto_purchase")}`,
          links: [
            {
              icon: <SwapIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
              name: `${t("sidebar.buy_sell")}`,
              link: "/exchange/",
              featureFlag: "ENABLE_EXCHANGE_FEATURE",
            },
          ],
        } as IMenuItem,
      ]
    : []),
  // Juridique
  {
    label: `${t("sidebar.legal")}`,
    links: [
      {
        icon: <FileTextIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.terms_of_use")}`,
        link: "/legal/terms-of-use/",
      },
      {
        icon: <FileTextIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.legal_notices")}`,
        link: "/legal/legal-notices/",
      },
      {
        icon: <CookieIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.cookie_management_policy")}`,
        link: "/legal/cookie-management-policy/",
      },
      {
        icon: <ReceiptIcon size={24} weight={"thin"} color={"rgb(116, 116, 127)"} />,
        name: `${t("sidebar.fees")}`,
        link: "/legal/fees/",
        featureFlag: "ENABLE_FEE_MANAGEMENT",
      },
    ],
  },
  // Déconnexion
  {
    label: null,
    class: "sidebar-section-no-padding",
    links: [
      {
        icon: <SignOutIcon size={24} weight="thin" color="#FF0000" />,
        name: `${t("sidebar.logout")}`,
        link: "/auth/login",
        class: "logout",
      },
    ],
  },
];

// Helper function to filter menu items based on feature flags
export const filterMenuByFeatures = (menu: IMenuItem[], features: Record<string, boolean | string>): IMenuItem[] => {
  return menu
    .map((section) => {
      if (!section.links) return section;

      const filteredLinks = section.links.filter((link) => {
        if (!link.featureFlag) return true;
        const featureValue = features[link.featureFlag];
        return featureValue === undefined || featureValue === true;
      });

      if (filteredLinks.length === 0 && section.links.length > 0) {
        return null;
      }

      return { ...section, links: filteredLinks };
    })
    .filter((section): section is IMenuItem => section !== null);
};
